import Debug from '../utils/Debug.js';
import TextureConfig from '../utils/TextureConfig.js';

/**
 * Material system for cuboid faces
 */
export default class Material {
    /** Set of active video elements for the global visibility listener */
    static _activeVideos = new Set();
    static _visibilityListenerRegistered = false;

    /** Audio unlock state — browsers require a user gesture before unmuted audio can play */
    static _audioUnlocked = false;
    static _audioUnlockRegistered = false;
    /** Shared AudioContext for all app audio (video, future SFX/music) */
    static audioContext = null;

    /**
     * Register the global visibilitychange listener (once).
     * Resumes all active videos when the tab becomes visible.
     */
    static _ensureVisibilityListener() {
      if (Material._visibilityListenerRegistered) return;
      Material._visibilityListenerRegistered = true;
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          for (const video of Material._activeVideos) {
            if (video.paused) {
              video.play().catch(() => {});
            }
          }
        }
      });
    }

    /**
     * Register a one-time first-interaction listener to unlock audio.
     * Browsers block unmuted autoplay until user gesture (click/touch/keydown).
     * On first interaction: creates AudioContext, unmutes tagged videos.
     */
    static _ensureAudioUnlock() {
      if (Material._audioUnlockRegistered) return;
      Material._audioUnlockRegistered = true;

      const unlock = () => {
        if (Material._audioUnlocked) return;
        Material._audioUnlocked = true;

        // Create shared AudioContext for future SFX/music
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx) {
          Material.audioContext = new AudioCtx();
          // Safari requires resume() after a user gesture
          if (Material.audioContext.state === 'suspended') {
            Material.audioContext.resume().catch(() => {});
          }
        }

        // Unmute all videos that were tagged as wanting unmuted
        for (const video of Material._activeVideos) {
          if (video._wantsMuted === false) {
            video.muted = false;
            delete video._wantsMuted;
            // Re-trigger play since some browsers pause unmuted autoplay attempts
            if (video.paused) {
              video.play().catch(() => {});
            }
          }
        }

        // Clean up listeners
        document.removeEventListener('pointerdown', unlock, true);
        document.removeEventListener('keydown', unlock, true);
      };

      // pointerdown covers left-click, right-click, touch, and pen
      document.addEventListener('pointerdown', unlock, true);
      document.addEventListener('keydown', unlock, true);
    }

    /**
     * Create a lightweight video element for fragment faces.
     * Has NO src attribute, so no WebMediaPlayer is created until srcObject is assigned.
     * @return {HTMLVideoElement} A styled video element ready for srcObject assignment
     */
    static createFragmentVideo() {
      const video = document.createElement('video');
      // No src - avoids creating a WebMediaPlayer until srcObject is set
      video.autoplay = true;
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
      video.disablePictureInPicture = true;
      video.setAttribute('disablepictureinpicture', '');
      video.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback');
      video.style.width = '100%';
      video.style.height = '100%';
      video.style.objectFit = 'cover';

      Material._activeVideos.add(video);
      Material._ensureVisibilityListener();
      return video;
    }

    /**
     * Clean up a video element to release its WebMediaPlayer.
     * Call before removing a video's parent from the DOM.
     * @param {HTMLVideoElement} video - The video element to clean up
     */
    static cleanupVideo(video) {
      if (!video) return;
      video._disposed = true;
      video.pause();
      if (video.srcObject) {
        // Don't stop shared stream tracks here — they belong to the parent.
        // Just disconnect this consumer.
        video.srcObject = null;
      }
      video.removeAttribute('src');
      video.load(); // Forces the browser to release the WebMediaPlayer
      Material._activeVideos.delete(video);
    }

    /**
     * Apply base material styles (border, opacity, className) without type-specific content.
     * Used for fragment video faces where we create a lightweight video separately.
     * @param {HTMLElement} element - The element to style
     * @param {number} zoom - Zoom factor
     */
    applyBaseStyles(element, zoom = 1) {
      element.style.opacity = this.opacity;
      const scaledBorderWidth = this.borderWidth * zoom;
      element.style.border = `${scaledBorderWidth}px ${this.borderStyle || 'solid'} ${this.borderColor}`;
      if (this.className) {
        element.classList.add(...this.className.split(' '));
      }
    }

    /**
     * Create a new material
     * @param {Object} options - Material options
     */
    constructor(options = {}) {
      this.type = options.type || 'color'; // color, texture, video, gradient, pattern, special, custom, webpage
      this.color = options.color || '#FFFFFF';
      this.opacity = typeof options.opacity === 'number' ? options.opacity : 1.0;
      this.texture = options.texture || null; // URL for image texture
      this.video = options.video || null; // URL for video texture
      this.text = options.text || null; // Text to display
      this.textColor = options.textColor || '#FFFFFF';
      this.textSize = options.textSize || '12px';
      this.textFont = options.textFont || 'Arial, sans-serif';
      this.customRender = options.customRender || null; // Custom render function
      this.gradient = options.gradient || null; // Gradient definition
      this.pattern = options.pattern || null; // Pattern definition
      this.borderColor = options.borderColor || 'rgba(0, 0, 0, 0.3)';
      this.borderWidth = options.borderWidth !== undefined ? options.borderWidth : 0;
      this.className = options.className || ''; // Additional CSS classes
      this.effectType = options.effectType || null; // For special effects: glass, metallic, holographic, neon
      this.metallicDirection = options.metallicDirection || '0deg'; // For metallic: '0deg' (H), '90deg' (V)
      this.holographicDirection = options.holographicDirection || '0deg'; // For holographic: '0deg' (H), '90deg' (V)

      // Scale and offset defaults for texture/video materials - ensures consistent rendering
      this.scaleX = options.scaleX !== undefined ? options.scaleX : 1;
      this.scaleY = options.scaleY !== undefined ? options.scaleY : 1;
      this.scaleZ = options.scaleZ !== undefined ? options.scaleZ : 1;
      this.offsetX = options.offsetX !== undefined ? options.offsetX : 0;
      this.offsetY = options.offsetY !== undefined ? options.offsetY : 0;
      this.offsetZ = options.offsetZ !== undefined ? options.offsetZ : 0;

      // Non-collision flag - cuboids with noCollision materials don't block movement (e.g., water)
      this.noCollision = options.noCollision || false;

      // Webpage/iframe material properties
      this.webpage = options.webpage || null; // URL for iframe content
      this.webpageInteractive = options.webpageInteractive !== false; // Allow pointer events (default true)

      // Non-subdivision flag - materials that can't be split across fragments (e.g., iframes)
      this.noSubdivide = options.noSubdivide || false;

      // Video playback properties (defaults: autoplay, loop, muted all true for safe autoplay)
      this.autoplay = options.autoplay !== undefined ? options.autoplay : true;
      this.loop = options.loop !== undefined ? options.loop : true;
      this.muted = options.muted !== undefined ? options.muted : true;
    }
  
    /**
     * Apply this material to a DOM element
     * @param {HTMLElement} element - The element to apply the material to
     * @param {number} zoom - Optional zoom factor for scaling (default 1)
     */
    apply(element, zoom = 1) {
      // Base styles
      element.style.opacity = this.opacity;
      // Scale border width with zoom so it grows/shrinks proportionally
      const scaledBorderWidth = this.borderWidth * zoom;
      element.style.border = `${scaledBorderWidth}px ${this.borderStyle || 'solid'} ${this.borderColor}`;

      if (this.className) {
        element.classList.add(...this.className.split(' '));
      }

      // Apply based on type
      switch (this.type) {
        case 'color':
          this.applyColor(element);
          break;
        case 'text':
          // Text type uses color as background, text is applied separately below
          this.applyColor(element);
          break;
        case 'texture':
          this.applyTexture(element, zoom);
          break;
        case 'video':
          this.applyVideo(element);
          break;
        case 'gradient':
          this.applyGradient(element, zoom);
          break;
        case 'pattern':
          this.applyPattern(element, zoom);
          break;
        case 'special':
          this.applySpecialEffect(element, zoom);
          break;
        case 'custom':
          if (this.customRender) this.customRender(element);
          break;
        case 'webpage':
          this.applyWebpage(element, zoom);
          break;
      }

      // Apply text if specified
      if (this.text) {
        this.applyText(element, zoom);
      }
    }

    /**
     * Apply this material with triplanar UV projection
     * @param {HTMLElement} element - The element to apply the material to
     * @param {Object} uvSettings - { backgroundPosition, backgroundSize }
     */
    applyWithTriplanar(element, uvSettings) {
      // Apply base material first
      this.apply(element);

      // Override background settings for textures
      if (this.type === 'texture' && uvSettings) {
        element.style.backgroundPosition = uvSettings.backgroundPosition;
        element.style.backgroundSize = uvSettings.backgroundSize;
        element.style.backgroundRepeat = 'repeat';
      }
    }

    /**
     * Apply color material
     * @param {HTMLElement} element - The element to apply to
     */
    applyColor(element) {
      element.style.backgroundColor = this.color;
    }
  
    /**
     * Apply texture material
     * @param {HTMLElement} element - The element to apply to
     * @param {number} zoom - Zoom factor for scaling offset to stay stable relative to face
     */
    applyTexture(element, zoom = 1) {
      if (this.texture) {
        // Resolve texture path through TextureConfig for resolution variants
        const resolvedPath = TextureConfig.resolvePath(this.texture);
        element.style.backgroundImage = `url(${resolvedPath})`;

        // Always use percentage-based sizing - user has full control via scale
        const scaleX = this.scaleX || 1;
        const scaleY = this.scaleY || 1;
        element.style.backgroundSize = `${scaleX * 100}% ${scaleY * 100}%`;
        element.style.backgroundRepeat = 'repeat';

        // Apply offset scaled by zoom so it stays stable relative to face size
        // (X/Y only - Z is for triplanar projection)
        const offsetX = (this.offsetX || 0) * zoom;
        const offsetY = (this.offsetY || 0) * zoom;
        element.style.backgroundPosition = `${offsetX}px ${offsetY}px`;
      }
    }
  
    /**
     * Apply video material
     * @param {HTMLElement} element - The element to apply to
     */
    applyVideo(element) {
        if (!this.video) return;

        // Check if there's already a video element
        const existingVideo = element.querySelector('video');

        // Preserve stream-based videos (fragment sharing parent via captureStream)
        if (existingVideo && existingVideo.srcObject) {
          // Still ensure audio source exists for unmuted materials
          if (this.muted === false) {
            this._ensureAudioSource();
          }
          return;
        }

        // Preserve videos with matching source URL
        if (existingVideo && existingVideo.src === this.video) {
          // Ensure audio source exists (may be first call, or URL may have changed)
          if (this.muted === false) {
            this._ensureAudioSource();
          }
          this._syncToAudioSource(existingVideo);
          return;
        }

        // Also check if src matches after URL normalization (handles relative vs absolute URLs)
        if (existingVideo) {
          const existingSrc = existingVideo.getAttribute('src');
          if (existingSrc === this.video) {
            if (this.muted === false) {
              this._ensureAudioSource();
            }
            this._syncToAudioSource(existingVideo);
            return;
          }
        }

        // Clean up existing video before replacing
        if (existingVideo) {
          Material.cleanupVideo(existingVideo);
        }

        // Clear existing content only if we need to create a new video
        element.innerHTML = '';

        // Create video element — ALL visual videos are always muted.
        // Audio is handled by a separate persistent audio source (see _ensureAudioSource).
        const video = document.createElement('video');
        video.src = this.video;
        video.autoplay = this.autoplay !== false;
        video.loop = this.loop !== false;
        video.muted = true;
        video.playsInline = true;
        video.disablePictureInPicture = true;
        video.setAttribute('disablepictureinpicture', '');
        video.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback');
        video.style.width = '100%';
        video.style.height = '100%';
        video.style.objectFit = 'cover';

        // Use requestVideoFrameCallback if available (Chrome 88+)
        if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) {
          const handleVideoFrame = (now, metadata) => {
            // Stop the loop if video was disposed
            if (video._disposed) return;

            // If we're near the end of the video, prepare for looping
            if (metadata.mediaTime > video.duration - 0.1) {
              video.currentTime = 0;
            }

            video.requestVideoFrameCallback(handleVideoFrame);
          };

          video.requestVideoFrameCallback(handleVideoFrame);
        }

        // Add video to element
        element.appendChild(video);

        // Register for global visibility listener (replaces per-video listeners)
        Material._activeVideos.add(video);
        Material._ensureVisibilityListener();

        // Create persistent audio source if material wants sound
        if (this.muted === false) {
          this._ensureAudioSource();
          this._syncToAudioSource(video);
        }
    }

    /**
     * Create a persistent off-DOM video element for audio playback.
     * This element lives outside the cuboid DOM, so k-d tree subdivision
     * and fragment creation/destruction don't affect audio continuity.
     */
    _ensureAudioSource() {
      // If audio source exists with a different URL, clean up and recreate
      if (this._audioSource) {
        if (this._audioSource.getAttribute('src') === this.video) return;
        // URL changed — clean up old source before creating new one
        this._cleanupAudioSource();
      }

      const audio = document.createElement('video');
      audio.src = this.video;
      audio.autoplay = this.autoplay !== false;
      audio.loop = this.loop !== false;
      audio.playsInline = true;
      // Invisible but still "rendered" — display:none may cause some browsers to not play.
      // Use fixed positioning with zero dimensions so it stays in layout but is invisible.
      audio.style.cssText = 'position:fixed;width:0;height:0;opacity:0;pointer-events:none;';

      if (Material._audioUnlocked) {
        audio.muted = false;
      } else {
        // Audio not unlocked yet — start muted, tag for later
        audio.muted = true;
        audio._wantsMuted = false;
        Material._ensureAudioUnlock();
      }

      // Append to body so the browser keeps it alive (detached elements
      // may not play reliably in all browsers)
      document.body.appendChild(audio);
      audio.play().catch(() => {});

      // Retry play when media is loaded — autoplay may not work for unmuted
      // cross-origin videos even after user gesture on some browsers
      audio.addEventListener('canplay', () => {
        if (!audio._disposed && audio.paused) {
          audio.play().catch(() => {});
        }
      }, { once: true });

      this._audioSource = audio;
      Material._activeVideos.add(audio);
      Material._ensureVisibilityListener();
    }

    /**
     * Sync a visual video element's currentTime to the audio source.
     * Called when visual videos are created or preserved during applyVideo.
     * @param {HTMLVideoElement} video - The visual video to sync
     */
    _syncToAudioSource(video) {
      if (!this._audioSource) return;
      const audioTime = this._audioSource.currentTime;
      if (audioTime && Math.abs(video.currentTime - audioTime) > 0.1) {
        video.currentTime = audioTime;
      }
    }

    /**
     * Clean up the persistent audio source for this material.
     * Call when the material is no longer in use.
     */
    _cleanupAudioSource() {
      if (!this._audioSource) return;
      Material.cleanupVideo(this._audioSource);
      if (this._audioSource.parentNode) {
        this._audioSource.parentNode.removeChild(this._audioSource);
      }
      this._audioSource = null;
    }

    /**
     * Maximum nesting depth for same-origin iframe inception
     * Beyond this depth, a placeholder is shown instead of another iframe
     */
    static MAX_IFRAME_DEPTH = 50;

    /**
     * Get the current iframe nesting depth from URL parameter
     * @return {number} Current depth (0 if not in a nested iframe)
     */
    static getIframeDepth() {
      const params = new URLSearchParams(window.location.search);
      return parseInt(params.get('_iframeDepth') || '0', 10);
    }

    /**
     * Check if a URL is same-origin as the current page
     * @param {string} url - URL to check
     * @return {boolean} True if same origin
     */
    static isSameOrigin(url) {
      try {
        const urlObj = new URL(url, window.location.origin);
        return urlObj.origin === window.location.origin;
      } catch {
        // Relative URLs are same-origin
        return true;
      }
    }

    /**
     * Normalize a webpage URL for iframe embedding
     * - Valid absolute URLs (http/https) are used as-is
     * - Special values like 'about:blank' are preserved
     * - Relative paths starting with './' or '/' are preserved
     * - Local file paths (file:///.html) are preserved
     * - Empty or invalid text defaults to current page URL for same-origin inception
     * - For same-origin URLs, adds depth tracking to prevent infinite recursion
     * @param {string} url - The URL to normalize
     * @return {string} Normalized URL with depth tracking for same-origin
     */
    static normalizeWebpageUrl(url) {
      if (!url || url.trim() === '') {
        // Empty - default to current page for same-origin inception
        return Material.addDepthToUrl(window.location.href);
      }

      const trimmed = url.trim();

      // Preserve special browser URLs
      if (trimmed.startsWith('about:') || trimmed.startsWith('data:')) {
        return trimmed;
      }

      // Valid absolute URLs
      if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        // Add depth tracking for same-origin URLs
        if (Material.isSameOrigin(trimmed)) {
          return Material.addDepthToUrl(trimmed);
        }
        return trimmed;
      }

      // Relative paths - preserve as-is but add depth tracking
      if (trimmed.startsWith('./') || trimmed.startsWith('/') || trimmed.endsWith('.html')) {
        return Material.addDepthToUrl(trimmed);
      }

      // File protocol (for local development)
      if (trimmed.startsWith('file://')) {
        return trimmed;
      }

      // Otherwise, assume it's random text - default to current page for same-origin inception
      // This ensures app-in-app works reliably on published versions
      return Material.addDepthToUrl(window.location.href);
    }

    /**
     * Add iframe depth tracking to a URL
     * @param {string} url - URL to modify
     * @return {string} URL with incremented depth parameter
     */
    static addDepthToUrl(url) {
      try {
        const currentDepth = Material.getIframeDepth();
        const newDepth = currentDepth + 1;

        // Parse URL and add/update depth parameter
        const urlObj = new URL(url, window.location.origin);
        urlObj.searchParams.set('_iframeDepth', newDepth.toString());
        return urlObj.toString();
      } catch {
        // If URL parsing fails, add parameter manually
        const currentDepth = Material.getIframeDepth();
        const newDepth = currentDepth + 1;
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}_iframeDepth=${newDepth}`;
      }
    }

    /**
     * Apply webpage/iframe material
     * @param {HTMLElement} element - The element to apply to
     * @param {number} zoom - Zoom factor for scaling (default 1, >1 on Android)
     */
    applyWebpage(element, zoom = 1) {
      if (!this.webpage) return;

      // Check if we've exceeded max nesting depth for same-origin iframes
      const currentDepth = Material.getIframeDepth();
      const isSameOrigin = Material.isSameOrigin(this.webpage) ||
                           !this.webpage.startsWith('http');

      if (isSameOrigin && currentDepth >= Material.MAX_IFRAME_DEPTH) {
        // Show placeholder instead of creating another nested iframe
        element.innerHTML = '';
        const placeholder = document.createElement('div');
        placeholder.className = 'webpage-container webpage-depth-limit';
        placeholder.style.cssText = `
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          color: #8892b0;
          font-family: system-ui, sans-serif;
          font-size: 12px;
          text-align: center;
          padding: 10px;
          box-sizing: border-box;
        `;
        placeholder.innerHTML = `<div>🔄 Inception depth limit<br><small style="opacity: 0.7">Level ${currentDepth}</small></div>`;
        element.appendChild(placeholder);
        return;
      }

      // Normalize the URL for reliable same-origin inception
      const normalizedUrl = Material.normalizeWebpageUrl(this.webpage);

      // Check if there's already an iframe with the same source
      const existingIframe = element.querySelector('iframe');
      if (existingIframe) {
        const existingSrc = existingIframe.getAttribute('src');
        if (existingSrc === normalizedUrl) {
          // Iframe already exists with same source - preserve it to maintain state
          // But update zoom transform if needed (for Android)
          const existingContainer = element.querySelector('.webpage-container');
          if (existingContainer && zoom !== 1) {
            existingContainer.style.transformOrigin = 'left top';
            existingContainer.style.transform = `scale(${zoom})`;
            existingContainer.style.width = `${100 / zoom}%`;
            existingContainer.style.height = `${100 / zoom}%`;
          }
          return;
        }
      }

      // Clear existing content only if we need to create a new iframe
      element.innerHTML = '';

      // Create container for proper positioning
      const container = document.createElement('div');
      container.className = 'webpage-container';
      container.style.position = 'absolute';
      container.style.top = '0';
      container.style.left = '0';
      container.style.overflow = 'hidden';

      // On Android (zoom > 1), scale container to match zoom
      // Container is smaller but scaled up, so iframe content appears zoomed
      if (zoom !== 1) {
        container.style.transformOrigin = 'left top';
        container.style.transform = `scale(${zoom})`;
        container.style.width = `${100 / zoom}%`;
        container.style.height = `${100 / zoom}%`;
      } else {
        container.style.width = '100%';
        container.style.height = '100%';
      }

      // Create iframe element
      const iframe = document.createElement('iframe');
      iframe.src = normalizedUrl;
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.style.border = 'none';
      iframe.style.userSelect = 'none'; // Prevent text selection drag issues
      iframe.setAttribute('draggable', 'false'); // Prevent iframe dragging

      // Control interactivity - this is the key difference from video materials
      // Set pointer-events on BOTH container and iframe to override CSS !important rules
      // (material-editor-mode sets pointer-events: none !important on .webpage-container)
      if (this.webpageInteractive) {
        container.style.pointerEvents = 'auto';
        iframe.style.pointerEvents = 'auto';

        // For same-origin iframes, we can add event handlers inside the iframe document
        // This allows us to prevent right-click context menu
        // For cross-origin iframes, this will fail silently due to browser security
        iframe.addEventListener('load', () => {
          try {
            const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

            // Prevent right-click context menu (but don't forward for camera rotation)
            // Camera rotation from inside iframe is intentionally not supported
            iframeDoc.addEventListener('contextmenu', (e) => {
              e.preventDefault();
              e.stopPropagation();
            });

            // Prevent drag
            iframeDoc.addEventListener('dragstart', (e) => {
              e.preventDefault();
            });

            // Prevent scroll chaining from iframe to parent.
            // When iframe content uses scrollIntoView() or hash navigation,
            // the browser can chain the scroll to parent frame elements.
            iframeDoc.documentElement.style.overscrollBehavior = 'contain';
            if (iframeDoc.body) {
              iframeDoc.body.style.overscrollBehavior = 'contain';
            }

            // Note: Wheel events are NOT forwarded - they scroll the iframe content instead
            // This matches expected behavior for interactive webpages

          } catch (err) {
            // Cross-origin iframe - cannot access document (browser security)
            // Events inside the iframe cannot be intercepted
          }
        });
      } else {
        container.style.pointerEvents = 'none';
        iframe.style.pointerEvents = 'none';
      }

      container.appendChild(iframe);
      element.appendChild(container);
    }

    /**
     * Apply gradient material
     * @param {HTMLElement} element - The element to apply to
     * @param {number} zoom - Zoom factor for scaling offset (default 1)
     */
    applyGradient(element, zoom = 1) {
      if (!this.gradient) return;

      const { type, colors, direction } = this.gradient;

      // Ensure we have valid colors
      if (!colors || colors.length === 0) return;
      const safeColors = colors.length === 1 ? [colors[0], colors[0]] : colors;

      const scaleX = this.scaleX || 1;
      const scaleY = this.scaleY || 1;
      // Scale offset by zoom (matches fragment behavior in Cuboid.applyGradientOffset)
      const offsetX = (this.offsetX || 0) * zoom;
      const offsetY = (this.offsetY || 0) * zoom;
      let gradientStr = '';

      if (type === 'linear') {
        const dir = direction || 'to bottom';
        const angle = typeof direction === 'number' ? `${direction}deg` : direction;
        gradientStr = `linear-gradient(${angle}, ${safeColors.join(', ')})`;

        // For linear gradients, use backgroundSize for scale
        if (scaleX !== 1 || scaleY !== 1) {
          element.style.backgroundSize = `${scaleX * 100}% ${scaleY * 100}%`;
          element.style.backgroundRepeat = 'repeat';
        }

        // Apply offset via backgroundPosition
        if (offsetX !== 0 || offsetY !== 0) {
          element.style.backgroundPosition = `${offsetX}px ${offsetY}px`;
        }
      } else if (type === 'radial') {
        // For radial gradients, use pixel-based approach (same as fragments/triplanar)
        const shape = direction || 'circle';
        const avgScale = (scaleX + scaleY) / 2;

        // Get element dimensions for pixel-based calculation
        // Try offsetWidth first (computed), then parse inline styles (avoids reflow issues)
        let elemWidth = element.offsetWidth;
        let elemHeight = element.offsetHeight;

        if (!elemWidth && element.style.width) {
          const match = element.style.width.match(/^(\d+(?:\.\d+)?)px$/);
          if (match) elemWidth = parseFloat(match[1]);
        }
        if (!elemHeight && element.style.height) {
          const match = element.style.height.match(/^(\d+(?:\.\d+)?)px$/);
          if (match) elemHeight = parseFloat(match[1]);
        }

        if (elemWidth && elemHeight) {
          // Use actual element dimensions so gradient stays "attached" to the face
          // and scales proportionally when zooming
          const halfW = elemWidth / 2;
          const halfH = elemHeight / 2;
          const maxRadius = Math.sqrt(halfW * halfW + halfH * halfH) / avgScale;

          // Center position in current (zoomed) pixels
          const centerX = elemWidth / 2 + offsetX;
          const centerY = elemHeight / 2 + offsetY;

          // Build pixel-based color stops using base dimensions
          // These stay fixed regardless of zoom
          const stops = safeColors.map((color, i) => {
            const t = i / (safeColors.length - 1);
            const radius = t * maxRadius;
            return `${color} ${radius}px`;
          });

          // Extend last color to ensure full coverage at all zoom levels
          stops.push(`${safeColors[safeColors.length - 1]} ${maxRadius * 3}px`);

          gradientStr = `radial-gradient(${shape} at ${centerX}px ${centerY}px, ${stops.join(', ')})`;
        } else {
          // Fallback: percentage-based approach when element not laid out yet
          let centerPos;
          if (offsetX !== 0 || offsetY !== 0) {
            const centerXStr = offsetX !== 0 ? `calc(50% + ${offsetX}px)` : '50%';
            const centerYStr = offsetY !== 0 ? `calc(50% + ${offsetY}px)` : '50%';
            centerPos = `at ${centerXStr} ${centerYStr}`;
          } else {
            centerPos = 'at 50% 50%';
          }

          let adjustedColors;
          if (avgScale !== 1 && safeColors.length > 1) {
            adjustedColors = safeColors.map((color, i) => {
              const match = color.match(/^(.+?)\s+(\d+)%$/);
              if (match) {
                const newPercent = Math.min(100, parseFloat(match[2]) / avgScale);
                return `${match[1]} ${newPercent}%`;
              }
              const basePercent = (i / (safeColors.length - 1)) * 100;
              const newPercent = Math.min(100, basePercent / avgScale);
              return `${color} ${newPercent}%`;
            });
          } else {
            adjustedColors = safeColors;
          }

          gradientStr = `radial-gradient(${shape} ${centerPos}, ${adjustedColors.join(', ')})`;
        }
      }

      element.style.backgroundImage = gradientStr;
    }
  
    /**
     * Apply pattern material
     * @param {HTMLElement} element - The element to apply to
     * @param {number} zoom - Zoom factor for scaling (default 1)
     */
    applyPattern(element, zoom = 1) {
      if (!this.pattern) return;

      // Apply pattern based on type - all sizes are multiplied by zoom
      switch (this.pattern.type) {
        case 'stripes':
          // Only H/V stripes supported (diagonal/rotated removed due to mobile rendering issues)
          const dir = this.pattern.direction || '0deg';
          const colors = this.pattern.colors || ['#FFF', '#DDD'];
          const stripeSize = (parseInt(this.pattern.size) || 10) * zoom;
          const stripePeriod = stripeSize * 2;

          // Use non-repeating gradient with background-size for H/V directions
          element.style.backgroundImage = `linear-gradient(
            ${dir},
            ${colors[0]} 0px,
            ${colors[0]} ${stripeSize}px,
            ${colors[1]} ${stripeSize}px,
            ${colors[1]} ${stripePeriod}px
          )`;
          // Set size based on direction
          if (dir === '0deg' || dir === '180deg') {
            element.style.backgroundSize = `100% ${stripePeriod}px`;
          } else {
            element.style.backgroundSize = `${stripePeriod}px 100%`;
          }
          element.style.backgroundRepeat = 'repeat';
          break;
        case 'dots':
          const bgColor = this.pattern.backgroundColor || '#FFF';
          const dotColor = this.pattern.dotColor || '#DDD';
          const dotSize = (parseInt(this.pattern.dotSize) || 5) * zoom;
          const dotSpacing = (parseInt(this.pattern.spacing) || 10) * zoom;
          element.style.backgroundColor = bgColor;
          element.style.backgroundImage = `radial-gradient(
            ${dotColor} ${dotSize}px,
            transparent ${dotSize}px
          )`;
          element.style.backgroundSize = `${dotSpacing}px ${dotSpacing}px`;
          element.style.backgroundRepeat = 'repeat';
          break;
        case 'grid':
          const gridBgColor = this.pattern.backgroundColor || '#FFF';
          const gridColor = this.pattern.gridColor || '#DDD';
          const gridSize = (parseInt(this.pattern.gridSize) || 10) * zoom;
          const lineWidth = Math.max(1, (parseInt(this.pattern.lineWidth) || 1) * zoom);
          element.style.backgroundColor = gridBgColor;
          element.style.backgroundImage = `
            linear-gradient(to right, ${gridColor} ${lineWidth}px, transparent ${lineWidth}px),
            linear-gradient(to bottom, ${gridColor} ${lineWidth}px, transparent ${lineWidth}px)
          `;
          element.style.backgroundSize = `${gridSize}px ${gridSize}px`;
          element.style.backgroundRepeat = 'repeat';
          break;
      }

      // Apply scale if specified (after pattern-specific styles)
      if (this.scaleX !== undefined || this.scaleY !== undefined) {
        const scaleX = this.scaleX || 1;
        const scaleY = this.scaleY || 1;
        const currentSize = element.style.backgroundSize || '100% 100%';
        // Parse current size and apply scale
        const sizeParts = currentSize.split(' ');
        if (sizeParts.length >= 2) {
          const baseX = parseFloat(sizeParts[0]);
          const baseY = parseFloat(sizeParts[1]);
          const newSizeX = `${baseX * scaleX}${sizeParts[0].includes('%') ? '%' : 'px'}`;
          const newSizeY = `${baseY * scaleY}${sizeParts[1].includes('%') ? '%' : 'px'}`;
          element.style.backgroundSize = `${newSizeX} ${newSizeY}`;
        }
      }

      // Apply offset if specified (X/Y only - don't scale by zoom for single-face)
      if (this.offsetX || this.offsetY) {
        const offsetX = this.offsetX || 0;
        const offsetY = this.offsetY || 0;
        element.style.backgroundPosition = `${offsetX}px ${offsetY}px`;
      }
    }

    /**
     * Apply special effect material (glass, metallic, holographic, neon)
     * @param {HTMLElement} element - The element to apply to
     * @param {number} zoom - Zoom factor for scaling (default 1)
     */
    applySpecialEffect(element, zoom = 1) {
      // Apply base color first
      element.style.backgroundColor = this.color;

      // Add the effect class
      if (this.effectType) {
        element.classList.add(this.effectType);
      }

      // Apply effect-specific styles - sizes scaled by zoom
      switch (this.effectType) {
        case 'glass':
          // Glass: transparent with backdrop blur
          // Note: opacity is already applied in base apply() method
          element.style.backdropFilter = 'blur(2px)';
          element.style.webkitBackdropFilter = 'blur(2px)';
          break;
        case 'metallic':
          // Metallic: shiny gradient overlay with H/V direction only
          element.classList.add('metallic');
          const metallicDir = this.metallicDirection || '0deg';
          const mp = 10 * zoom; // metallic period base
          element.style.backgroundImage = `linear-gradient(
            ${metallicDir},
            rgba(255, 255, 255, 0.35) 0px,
            rgba(255, 255, 255, 0.1) ${mp}px,
            rgba(0, 0, 0, 0.1) ${mp * 2}px,
            rgba(255, 255, 255, 0.1) ${mp * 3}px,
            rgba(255, 255, 255, 0.35) ${mp * 4}px
          )`;
          element.style.backgroundSize = `${mp * 4}px ${mp * 4}px`;
          element.style.backgroundRepeat = 'repeat';
          break;
        case 'holographic':
          // Holographic: rainbow gradient with H/V direction only
          element.classList.add('holographic');
          const holoDir = this.holographicDirection || '0deg';
          const hp = 10 * zoom; // holo period base
          element.style.backgroundImage = `linear-gradient(
            ${holoDir},
            rgb(255, 0, 0) 0px,
            rgb(255, 127, 0) ${hp}px,
            rgb(255, 255, 0) ${hp * 2}px,
            rgb(0, 255, 0) ${hp * 3}px,
            rgb(0, 127, 255) ${hp * 4}px,
            rgb(0, 0, 255) ${hp * 5}px,
            rgb(148, 0, 211) ${hp * 6}px,
            rgb(255, 0, 0) ${hp * 7}px
          )`;
          element.style.backgroundSize = `${hp * 7}px ${hp * 7}px`;
          element.style.backgroundRepeat = 'repeat';
          break;
        case 'neon':
          // Neon: glowing effect with the base color
          element.style.color = this.color; // For currentColor in CSS
          // Apply the neon glow box-shadow directly for better compatibility
          const gs = 10 * zoom; // glow size base
          element.style.boxShadow = `
            0 0 ${gs}px ${this.color},
            0 0 ${gs * 2}px ${this.color},
            0 0 ${gs * 3}px ${this.color},
            inset 0 0 ${gs}px ${this.color}
          `;
          break;
      }
    }

    /**
     * Apply text to element
     * @param {HTMLElement} element - The element to apply to
     * @param {number} zoom - Zoom factor for scaling text size (default 1)
     */
    applyText(element, zoom = 1) {
      // Create or get text container
      let textContainer = element.querySelector('.face-text');
      if (!textContainer) {
        textContainer = document.createElement('div');
        textContainer.className = 'face-text';
        element.appendChild(textContainer);
      }

      // Parse the base font size and scale by zoom and scaleX/scaleY
      const baseFontSize = parseFloat(this.textSize) || 12;
      const fontUnit = this.textSize.replace(/[\d.]/g, '') || 'px';
      const scaleX = this.scaleX || 1;
      const scaleY = this.scaleY || 1;
      // Use average of scaleX and scaleY for uniform text scaling
      const textScale = (scaleX + scaleY) / 2;
      const scaledFontSize = baseFontSize * zoom * textScale;

      // Apply offset scaled by zoom (so it stays stable relative to face size)
      const offsetX = (this.offsetX || 0) * zoom;
      const offsetY = (this.offsetY || 0) * zoom;

      // Style text container
      textContainer.style.position = 'absolute';
      textContainer.style.top = '0';
      textContainer.style.left = '0';
      textContainer.style.width = '100%';
      textContainer.style.height = '100%';
      textContainer.style.display = 'flex';
      textContainer.style.justifyContent = 'center';
      textContainer.style.alignItems = 'center';
      textContainer.style.color = this.textColor;
      textContainer.style.fontSize = `${scaledFontSize}${fontUnit}`;
      textContainer.style.fontFamily = this.textFont;
      textContainer.style.textAlign = 'center';
      textContainer.style.whiteSpace = 'nowrap'; // Prevent wrapping - match fragment styling
      textContainer.style.pointerEvents = 'none'; // Don't interfere with interactions

      // Apply transform for offset positioning
      if (offsetX !== 0 || offsetY !== 0) {
        textContainer.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
      } else {
        textContainer.style.transform = '';
      }

      // Set text content
      textContainer.textContent = this.text;
    }
  
    /**
     * Create a basic color material
     * @param {string} color - The color
     * @param {number} opacity - The opacity (0-1)
     * @return {Material} The created material
     */
    static color(color, opacity = 1.0) {
      return new Material({
        type: 'color',
        color,
        opacity
      });
    }
  
    /**
     * Create a texture material
     * @param {string} textureUrl - The texture URL
     * @param {number} opacity - The opacity (0-1)
     * @return {Material} The created material
     */
    static texture(textureUrl, opacity = 1.0) {
      return new Material({
        type: 'texture',
        texture: textureUrl,
        opacity,
        // Explicit defaults for consistent rendering
        scaleX: 1,
        scaleY: 1,
        scaleZ: 1,
        offsetX: 0,
        offsetY: 0,
        offsetZ: 0
      });
    }
  
    /**
     * Create a video material
     * @param {string} videoUrl - The video URL
     * @param {number} opacity - The opacity (0-1)
     * @return {Material} The created material
     */
    static video(videoUrl, opacity = 1.0) {
      return new Material({
        type: 'video',
        video: videoUrl,
        opacity,
        // Explicit defaults for consistent rendering
        scaleX: 1,
        scaleY: 1,
        scaleZ: 1,
        offsetX: 0,
        offsetY: 0,
        offsetZ: 0
      });
    }

    /**
     * Create a webpage/iframe material
     * @param {string} webpageUrl - The URL to embed in an iframe
     * @param {number} opacity - The opacity (0-1)
     * @param {Object} options - Additional options (interactive)
     * @return {Material} The created material
     */
    static webpage(webpageUrl, opacity = 1.0, options = {}) {
      return new Material({
        type: 'webpage',
        webpage: webpageUrl,
        opacity,
        webpageInteractive: options.interactive !== false,
        noSubdivide: true // Iframes cannot be split across fragments
      });
    }

    /**
     * Create a gradient material
     * @param {string} gradientType - 'linear' or 'radial'
     * @param {string[]} colors - Array of colors
     * @param {string} direction - Direction or shape
     * @param {number} opacity - The opacity (0-1)
     * @return {Material} The created material
     */
    static gradient(gradientType, colors, direction, opacity = 1.0) {
      return new Material({
        type: 'gradient',
        gradient: {
          type: gradientType,
          colors,
          direction
        },
        opacity
      });
    }
  
    /**
     * Create a pattern material
     * @param {string} patternType - 'stripes', 'dots', or 'grid'
     * @param {Object} options - Pattern options
     * @param {number} opacity - The opacity (0-1)
     * @return {Material} The created material
     */
    static pattern(patternType, options, opacity = 1.0) {
      return new Material({
        type: 'pattern',
        pattern: {
          type: patternType,
          ...options
        },
        opacity
      });
    }

    /**
     * Create a special effect material
     * @param {string} effectType - 'glass', 'metallic', 'holographic', or 'neon'
     * @param {string} color - Base color
     * @param {number} opacity - The opacity (0-1)
     * @param {Object} options - Additional options (metallicDirection, holographicDirection)
     * @return {Material} The created material
     */
    static special(effectType, color = '#3498db', opacity = 1.0, options = {}) {
      return new Material({
        type: 'special',
        effectType,
        color,
        opacity,
        metallicDirection: options.metallicDirection,
        holographicDirection: options.holographicDirection
      });
    }

    /**
     * Serialize material to a plain object for JSON storage
     * @return {Object} Serializable material data
     */
    toJSON() {
      const data = {
        type: this.type
      };

      // Always include color if not default white
      if (this.color !== '#FFFFFF') {
        data.color = this.color;
      }

      // Include opacity if not default 1.0
      if (this.opacity !== 1.0) {
        data.opacity = this.opacity;
      }

      // Include border settings if non-default
      if (this.borderColor !== 'rgba(0, 0, 0, 0.3)') {
        data.borderColor = this.borderColor;
      }
      if (this.borderWidth !== 0) {
        data.borderWidth = this.borderWidth;
      }
      if (this.borderStyle) {
        data.borderStyle = this.borderStyle;
      }

      // Include noCollision if true
      if (this.noCollision) {
        data.noCollision = true;
      }

      // Include noSubdivide if true
      if (this.noSubdivide) {
        data.noSubdivide = true;
      }

      // Type-specific properties
      switch (this.type) {
        case 'color':
          data.color = this.color;
          break;

        case 'texture':
          data.texture = this.texture;
          if (this.scaleX !== undefined) data.scaleX = this.scaleX;
          if (this.scaleY !== undefined) data.scaleY = this.scaleY;
          if (this.scaleZ !== undefined) data.scaleZ = this.scaleZ;
          if (this.offsetX !== undefined) data.offsetX = this.offsetX;
          if (this.offsetY !== undefined) data.offsetY = this.offsetY;
          break;

        case 'video':
          data.video = this.video;
          if (this.autoplay === false) data.autoplay = false;
          if (this.loop === false) data.loop = false;
          if (this.muted === false) data.muted = false;
          break;

        case 'gradient':
          data.gradient = this.gradient;
          if (this.scaleX !== undefined) data.scaleX = this.scaleX;
          if (this.scaleY !== undefined) data.scaleY = this.scaleY;
          if (this.offsetX !== undefined) data.offsetX = this.offsetX;
          if (this.offsetY !== undefined) data.offsetY = this.offsetY;
          break;

        case 'pattern':
          data.pattern = this.pattern;
          if (this.scaleX !== undefined) data.scaleX = this.scaleX;
          if (this.scaleY !== undefined) data.scaleY = this.scaleY;
          if (this.offsetX !== undefined) data.offsetX = this.offsetX;
          if (this.offsetY !== undefined) data.offsetY = this.offsetY;
          if (this.rotation !== undefined) data.rotation = this.rotation;
          break;

        case 'special':
          data.effectType = this.effectType;
          data.color = this.color;
          if (this.effectType === 'metallic') {
            if (this.metallicDirection) data.metallicDirection = this.metallicDirection;
          }
          if (this.effectType === 'holographic') {
            if (this.holographicDirection) data.holographicDirection = this.holographicDirection;
          }
          break;

        case 'text':
          data.color = this.color;
          if (this.scaleX !== undefined) data.scaleX = this.scaleX;
          if (this.scaleY !== undefined) data.scaleY = this.scaleY;
          if (this.scaleZ !== undefined) data.scaleZ = this.scaleZ;
          if (this.offsetX !== undefined) data.offsetX = this.offsetX;
          if (this.offsetY !== undefined) data.offsetY = this.offsetY;
          if (this.offsetZ !== undefined) data.offsetZ = this.offsetZ;
          if (this.axis !== undefined) data.axis = this.axis;
          break;

        case 'webpage':
          data.webpage = this.webpage;
          if (this.webpageInteractive !== undefined) data.webpageInteractive = this.webpageInteractive;
          break;
      }

      // Include text if specified (for any material type that has text overlay)
      if (this.text) {
        data.text = this.text;
        data.textColor = this.textColor;
        data.textSize = this.textSize;
        data.textFont = this.textFont;
      }

      // Include className if specified
      if (this.className) {
        data.className = this.className;
      }

      return data;
    }

    /**
     * Create a Material instance from JSON data
     * @param {Object} data - Material data from JSON
     * @return {Material} The created material
     */
    static fromJSON(data) {
      return new Material(data);
    }
  }